import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { Add } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useFormikContext } from 'formik';

import { SuccessDialog } from '../SuccessDialog';

import { STATION_TYPE, SUCCESS_DIALOG } from '~common';
import { PaddingBox } from '~components/atoms/custom';
import { PlacementReviewCard, VenueReviewCard } from '~components/molecules';
import { useQueryGetReason } from '~hooks/reason/useQueryGetReason';
import { useVenuePlacementCreationContext } from '~providers';
import {
  PINNING_STEP,
  usePinningStepContext,
} from '~providers/PinningStepProvider';
import {
  IFormCreationValues,
  STATION_PLACEMENT_ASSIGN_INITIAL_VALUES,
  useFormCreateContext,
} from '~providers/useForm';
import { UnpinningReasonKey } from '~types';
import { getStationInfoDisplay } from '~utils/station.util';
import { getPlacementUnpinningDetail } from '~utils/unpinning.util';

const reasonQueryParams = (key: UnpinningReasonKey) => ({
  page: 0,
  size: 20,
  filter: { filterByKeys: [key] },
});

// Wrap the component with Suspense and provide the fallback
export const ReviewAndConfirmStep: React.FC = () => (
  <Suspense
    fallback={
      <Box
        display="flex"
        width="100vw"
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    }
  >
    <ReviewAndConfirmStepView />
  </Suspense>
);

const ReviewAndConfirmStepView: React.FC = () => {
  const {
    handleSetPinningStep,
    handleSetSelectedPlacementId,
    setIsAddingFromReview,
  } = usePinningStepContext();
  const {
    placementListInfo,
    existingVenueDetail,
    handleSelectPlacement,
    handleRemovePlacement,
  } = useVenuePlacementCreationContext();

  const {
    isFormSubmitSuccessDialogOpen,
    closeFormSubmitSuccessDialog,
    successDialogContent,
    isFormSubmitLoading,
  } = useFormCreateContext();

  const { values, setFieldValue, submitForm, setTouched } =
    useFormikContext<IFormCreationValues>();

  // A placement stores only the reason id, so both lists are needed to turn it back
  // into a label: swap and relocation are fetched separately. Queried once here rather
  // than per card, and the confirm steps have already warmed the same cache.
  const swapReasons = useQueryGetReason(
    reasonQueryParams(UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_SWAPS),
  );
  const relocationReasons = useQueryGetReason(
    reasonQueryParams(
      UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_RELOCATION,
    ),
  );

  const reasonLabelById = useMemo(() => {
    const allReasons = [
      ...(swapReasons.data?.data ?? []),
      ...(relocationReasons.data?.data ?? []),
    ];
    return new Map(allReasons.map((reason) => [reason.id, reason.reason]));
  }, [swapReasons.data, relocationReasons.data]);

  const handleOnClickAddNewPlacement = useCallback(() => {
    setFieldValue('placement', STATION_PLACEMENT_ASSIGN_INITIAL_VALUES);
    setIsAddingFromReview(true);
    handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT);
  }, [handleSetPinningStep, setFieldValue, setIsAddingFromReview]);

  const handleOnClickPlacementInfo = (index: number) => {
    handleSelectPlacement(index);
    handleSetPinningStep(PINNING_STEP.PLACEMENT_DETAIL_REVIEW);
  };

  // Alway update the placement formik's value up to date with the last placement
  useEffect(() => {
    const lastPlacement =
      placementListInfo.at(-1) ?? STATION_PLACEMENT_ASSIGN_INITIAL_VALUES;
    setFieldValue('placement', lastPlacement);
    handleSetSelectedPlacementId(lastPlacement.placementId);
    // Set formik's touched status to false for add new placement or update venue-placement
    setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementListInfo]);

  return (
    <>
      <PaddingBox
        flex={1}
        sx={{
          bgcolor: 'background.paper',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          pb: 16,
        }}
      >
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={2}
          width="100%"
        >
          <VenueReviewCard
            venueTypeName={
              existingVenueDetail?.venueTypeName ||
              values.venue.venueType?.name ||
              ''
            }
            venueSubTypeName={
              existingVenueDetail?.venueSubTypeName ||
              values.venue.venueSubType?.name ||
              'None'
            }
            venueName={
              existingVenueDetail?.locationName ||
              values.venue.locationName ||
              ''
            }
            venueAddress={
              existingVenueDetail?.addressLine1 ||
              values.venue.address?.name ||
              ''
            }
            onClick={() =>
              handleSetPinningStep(PINNING_STEP.VENUE_DETAIL_REVIEW)
            }
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={600}>
              {`${placementListInfo.length} ${
                placementListInfo.length === 1 ? 'Placement' : 'Placements'
              }`}
            </Typography>
            <Button
              startIcon={<Add />}
              variant="outlined"
              sx={{
                borderRadius: 100,
                paddingTop: '6px',
                paddingBottom: '6px',
              }}
              onClick={handleOnClickAddNewPlacement}
            >
              Add
            </Button>
          </Stack>
          {placementListInfo.map((item, index) => {
            const stationImageUrl = getStationInfoDisplay(
              item.station?.type as STATION_TYPE,
            ).imageUrl;
            const { reasonId, note } = getPlacementUnpinningDetail(item);
            return (
              <PlacementReviewCard
                key={index}
                index={index}
                stationImageUrl={stationImageUrl}
                placementName={item.name}
                station={item.station}
                onClick={handleOnClickPlacementInfo}
                onRemovePlacement={handleRemovePlacement}
                unpinningReason={
                  reasonId ? reasonLabelById.get(reasonId) : undefined
                }
                unpinningNote={note}
              />
            );
          })}
        </Box>
      </PaddingBox>
      <PaddingBox
        alignItems="center"
        bgcolor="background.paper"
        position="fixed"
        bottom={0}
        width="100%"
      >
        <Button
          fullWidth
          size="large"
          variant="contained"
          style={{ borderRadius: '100px' }}
          onClick={submitForm}
          disabled={placementListInfo.length === 0}
        >
          <Typography variant="body2" fontWeight={500}>
            Submit for Admin review
          </Typography>
        </Button>
      </PaddingBox>
      {isFormSubmitLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', // Dim the background with opacity
            zIndex: 9999, // Ensure it's on top of all elements
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <SuccessDialog
        title={SUCCESS_DIALOG.FORM_SUBMISSION.TITLE}
        subtitle={successDialogContent}
        open={isFormSubmitSuccessDialogOpen}
        onClose={closeFormSubmitSuccessDialog}
        onBtnClick={closeFormSubmitSuccessDialog}
        btnText={SUCCESS_DIALOG.FORM_SUBMISSION.BUTTON_TEXT}
      />
    </>
  );
};
